import React, { useEffect, useState, useMemo } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { UserPlus, Loader2, Edit2, Trash2, KeyRound, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [products, setProducts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [notificationEmailInput, setNotificationEmailInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    id: null,
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'Engineer',
    product_ids: [],
    notification_emails: [],
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputValue);
      setCurrentPage(1);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    fetchUsers();
    fetchProducts();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editMode) {
        // For edit, don't send password if empty
        const updateData = {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          product_ids: formData.product_ids,
          notification_emails: formData.notification_emails,
        };
        await api.put(`/users/${formData.id}`, updateData);
        toast.success('User updated successfully!');
      } else {
        await api.post('/users', formData);
        toast.success('User created successfully!');
      }
      setDialogOpen(false);
      setFormData({ id: null, username: '', email: '', password: '', full_name: '', role: 'Engineer', product_ids: [], notification_emails: [] });
      setNotificationEmailInput('');
      setEditMode(false);
      setCurrentPage(1);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editMode ? 'Failed to update user' : 'Failed to create user'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      id: user.id,
      username: user.username,
      email: user.email,
      password: '',
      full_name: user.full_name || '',
      role: user.role,
      product_ids: user.product_ids || [],
      notification_emails: user.notification_emails || [],
    });
    setEditMode(true);
    setNotificationEmailInput('');
    setDialogOpen(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddNotificationEmail = () => {
    const email = notificationEmailInput.trim();
    
    if (!email) {
      return;
    }
    
    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (formData.notification_emails.includes(email)) {
      toast.error('This email is already added');
      return;
    }
    
    setFormData({
      ...formData,
      notification_emails: [...formData.notification_emails, email]
    });
    setNotificationEmailInput('');
  };

  const handleRemoveNotificationEmail = (emailToRemove) => {
    setFormData({
      ...formData,
      notification_emails: formData.notification_emails.filter(email => email !== emailToRemove)
    });
  };

  const handleAddNew = () => {
    setNotificationEmailInput('');
    setFormData({ id: null, username: '', email: '', password: '', full_name: '', role: 'Engineer', product_ids: [], notification_emails: [] });
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await api.put(`/users/${userToDelete.id}`, { is_active: false });
      toast.success('User deleted successfully!');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setCurrentPage(1);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setResettingPassword(true);
    try {
      await api.put(`/users/${userToReset.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      setResetPasswordDialogOpen(false);
      setUserToReset(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const roleColors = {
    'Admin': 'bg-red-100 text-red-700 border-red-300',
    'Manager': 'bg-purple-100 text-purple-700 border-purple-300',
    'Engineer': 'bg-blue-100 text-blue-700 border-blue-300',
    'Client': 'bg-green-100 text-green-700 border-green-300',
  };

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    return users.filter(user => 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [users, searchTerm]);

  // Paginate filtered users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  return (
    <div className="space-y-4 md:space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between gap-4 pb-4 border-slate-200 dark:border-slate-700">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Users</h1>
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400 dark:text-slate-500" />
            <Input
              placeholder="Search by name, email, or username..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="pl-9 text-xs sm:text-sm dark:bg-slate-800 dark:text-white dark:placeholder-slate-400 dark:border-slate-700"
            />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleAddNew} className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 shadow-md shadow-indigo-500/25 text-white text-xs sm:text-sm whitespace-nowrap">
                <UserPlus className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Add User
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] bg-white dark:bg-slate-900 dark:border-slate-800 p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg text-slate-900 dark:text-white">{editMode ? 'Edit User' : 'Create New User'}</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">{editMode ? 'Update user details' : 'Add a new user to the system'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-3 py-3 sm:py-4 max-h-[calc(100vh-250px)] overflow-y-auto pr-3">
                <div className="space-y-1.5">
                  <Label htmlFor="username" className="text-xs sm:text-sm dark:text-slate-200">Username *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    disabled={editMode}
                    required={!editMode}
                    className="text-xs sm:text-sm"
                  />
                  {editMode && <p className="text-xs text-slate-500 dark:text-slate-400">Username cannot be changed</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs sm:text-sm dark:text-slate-200">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="text-xs sm:text-sm"
                  />
                </div>
                {!editMode && (
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-xs sm:text-sm dark:text-slate-200">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="text-xs sm:text-sm"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="full_name" className="text-xs sm:text-sm dark:text-slate-200">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="text-xs sm:text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="role" className="text-xs sm:text-sm dark:text-slate-200">Role *</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger className="text-xs sm:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => (
                        <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.role === 'Client' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">Assigned Products *</Label>
                    <div className="border border-slate-300 rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                      {products.length === 0 ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400">No products available</p>
                      ) : (
                        products.map((product) => (
                          <div key={product.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`product-${product.id}`}
                              checked={formData.product_ids.includes(product.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setFormData({
                                    ...formData,
                                    product_ids: [...formData.product_ids, product.id]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    product_ids: formData.product_ids.filter(id => id !== product.id)
                                  });
                                }
                              }}
                            />
                            <label
                              htmlFor={`product-${product.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {product.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Select one or more products for this client</p>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm dark:text-slate-200">Notification Email IDs</Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Add additional emails to receive alert notifications</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter email address"
                      value={notificationEmailInput}
                      onChange={(e) => setNotificationEmailInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddNotificationEmail();
                        }
                      }}
                      className="text-xs sm:text-sm flex-1"
                    />
                    <Button
                      type="button"
                      onClick={handleAddNotificationEmail}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm px-3"
                    >
                      Add
                    </Button>
                  </div>
                  {formData.notification_emails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.notification_emails.map((email) => (
                        <Badge key={email} className="bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700 flex items-center gap-2 px-3 py-1">
                          {email}
                          <button
                            type="button"
                            onClick={() => handleRemoveNotificationEmail(email)}
                            className="ml-1 hover:font-bold text-sm text-blue-700 dark:text-blue-200 cursor-pointer"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {editMode ? 'Update User' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="border-slate-200 dark:bg-slate-900/80 dark:border-slate-800 shadow-sm">
        <CardContent className="space-y-4 pt-6">
          {loading ? (
            <div className="text-center py-8 text-slate-600 dark:text-slate-400">Loading...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-600 dark:text-slate-400">
              {searchTerm ? 'No users found matching your search' : 'No users available'}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-900/50">
                      <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Username</TableHead>
                      <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Full Name</TableHead>
                      <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Email</TableHead>
                      <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Role</TableHead>
                      <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Status</TableHead>
                      <TableHead className="text-slate-700 dark:text-slate-300 font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedUsers.map((user) => (
                      <TableRow key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                        <TableCell className="text-slate-900 dark:text-slate-100 font-medium">@{user.username}</TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">{user.full_name || '-'}</TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300 text-sm">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={roleColors[user.role] || 'bg-slate-100 text-slate-700 border-slate-300'}>{user.role}</Badge>
                        </TableCell>
                        <TableCell>
                          {!user.is_active ? (
                            <Badge variant="outline" className="bg-slate-100">Inactive</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 border-green-300">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(user)}
                              title="Edit user"
                            >
                              <Edit2 size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setUserToReset(user);
                                setResetPasswordDialogOpen(true);
                              }}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              title="Reset password"
                            >
                              <KeyRound size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setUserToDelete(user);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete user"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

              {/* Pagination Controls */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Showing <span className="font-semibold">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredUsers.length)}</span> to <span className="font-semibold">{Math.min(currentPage * itemsPerPage, filteredUsers.length)}</span> of <span className="font-semibold">{filteredUsers.length}</span> users
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} className="mr-1" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalPages }).map((_, i) => {
                      const page = i + 1;
                      // Show current page and adjacent pages
                      if (Math.abs(page - currentPage) <= 1 || page === 1 || page === totalPages) {
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className="w-9 h-9 p-0"
                          >
                            {page}
                          </Button>
                        );
                      } else if (Math.abs(page - currentPage) === 2) {
                        return <span key={`ellipsis-${page}`} className="text-slate-400">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[400px] bg-white dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for "{userToReset?.full_name || userToReset?.username}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="dark:text-slate-200">New Password *</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="dark:text-slate-200">Confirm Password *</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setNewPassword('');
                setConfirmPassword('');
              }}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={handleResetPassword}
              disabled={resettingPassword || !newPassword || !confirmPassword}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {resettingPassword ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-slate-900 dark:border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{userToDelete?.full_name || userToDelete?.username}"? This will deactivate the user account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;